import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { EVENT_TIMEOUT_MS } from '@/features/navigation/utils/constants';
import { findAndFocusElement } from '@/features/navigation/utils/find-and-focus-element';
import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { DropdownLinksWrapper } from './dropdown-links-wrapper';
import { DropDownNavigationItem } from './dropdown-navigation-item';
import { LinkToEntireCategory } from './link-to-entire-category';
import { LinksWrapper } from './links-wrapper';

interface Props {
  depthLevel: number;
  submenu: Navigation;
}
const selector = ({ setSubcategoryId, categoryId }: NavigationStore) => ({
  setSubcategoryId,
  categoryId,
});

export const Dropdown = ({ depthLevel, submenu }: Props) => {
  depthLevel = depthLevel + 1;
  const { children, href } = submenu;
  const [isHovered, setIsHovered] = useState(false);
  const { setSubcategoryId, categoryId } = useNavigationStore(selector);
  const isCurrentCategoryActive = categoryId === submenu.id;

  const isAnyItemHaveChildren = children.some(
    (dropdownNavElement) => dropdownNavElement?.children?.length >= 1,
  );
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const onMouseEnter = (id: number, isKeyboard?: boolean) => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(
      () => {
        setIsHovered(false);
        setSubcategoryId(id);
        if (isKeyboard) {
          setTimeout(() => {
            findAndFocusElement('[data-subcategory-first-item="true"]');
          }, 0);
        }
      },
      isKeyboard ? 0 : EVENT_TIMEOUT_MS,
    );
  };

  const onMouseLeave = () => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      if (isHovered) return;
      setSubcategoryId(null);
      setIsHovered(false);
    }, EVENT_TIMEOUT_MS);
  };

  const onEnterUlElement = () => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setSubcategoryId(null);
      setIsHovered(false);
    }, EVENT_TIMEOUT_MS);
  };

  const onEnterDropdownLinksWrapper = () => {
    setIsHovered(true);
    if (timeout.current) clearTimeout(timeout.current);
  };

  if (!isAnyItemHaveChildren)
    return <LinksWrapper submenu={submenu} depthLevel={depthLevel} />;

  return (
    <div
      className={clsx(
        '-left-m -z-10 absolute top-full overflow-hidden rounded-l border-3 border-decorative bg-main',
        isCurrentCategoryActive ? 'flex' : 'hidden',
      )}
      data-testid={'submenu'}
      data-isopen={isCurrentCategoryActive}
    >
      <ul
        onMouseEnter={onEnterUlElement}
        className={clsx(
          'w-max border-decorative bg-main px-m py-xs',
          isCurrentCategoryActive && 'border-r',
        )}
        hidden={!isCurrentCategoryActive}
      >
        {href && (
          <LinkToEntireCategory
            href={href}
            divider
            data-category-first-item={isCurrentCategoryActive}
          />
        )}

        {children?.map((submenu, index, row) => (
          <DropDownNavigationItem
            item={submenu}
            key={submenu.id}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            data-category-first-item={isCurrentCategoryActive && !href}
            data-category-last-item={
              index === row.length - 1 && isCurrentCategoryActive
            }
          />
        ))}
      </ul>
      <DropdownLinksWrapper
        depthLevel={depthLevel}
        submenu={submenu}
        onMouseEnter={onEnterDropdownLinksWrapper}
      />
    </div>
  );
};
